import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CATEGORIES, CAT_BY_ID, DEFAULT_CUSTOM_CATS, CAT_TREE } from '../data/categories.js';
import { SLOTS, SLOT_COUNT, emptyDay, isEmptyDay, parseDate, dateKey } from '../data/slots.js';
import { SAMPLE_DATA } from '../data/sample.js';
import { importCsv, exportCsv, importCycleHistory } from '../io/csv.js';
import { exportBackup, importBackup } from '../io/backup.js';
import { exportPng } from '../io/png.js';
import { importTemplate, exportTemplate, exportTemplateForDate, exportBlankTemplate, exportData3Sheet } from '../io/xlsx/index.jsx';
import { DailyTable } from './dailyTable.jsx';
import { Dashboard } from './dashboard.jsx';
import { MonthlyView, MonthYearNav, MonthDetailPanel } from './monthlyView.jsx';
import { AlbumTab } from './album.jsx';
import { CategoryPanel, DayMetaPanel } from './panels.jsx';
import { Pomodoro, PomodoroAudio, usePomodoroState, usePomodoroAudioChoice } from './pomodoro.jsx';
import { TweaksPanel, TweakSection, TweakRadio, TweakSelect, useTweaks } from './tweaks-panel.jsx';
import { CheckinTool } from './checkin.jsx';
import { LineIcon } from './icons.jsx';

const STORAGE_KEY = 'timeblock-app-v1';
// 种子版本号 —— 每次 DEFAULT_CUSTOM_CATS 新增/改色时递增
// 老设备升级时会一次性补种缺失项；用户删了不会复活（除非再次递增）
// v2：sleep_lazy 颜色从 var(--c-sleep) 改成 #DBC9F0（用户原赖床配色）
// v3：补种之前丢失的 5 个种子（exer_run / focus_en / focus_py / social_tra / social_help）
// v6：social_help 帮忙 block false→true（线框改填充，原色太淡；与娱乐·拉对换样式）
const SEEDS_VERSION = 6;

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw);
    if (state && state.customCats) {
      state.customCats.forEach(c => {
        if (!CAT_BY_ID[c.id]) {
          CAT_BY_ID[c.id] = c;
          CATEGORIES.push(c);
        }
      });
    }
    if (state && state.catOverrides) {
      Object.entries(state.catOverrides).forEach(([id, patch]) => {
        const target = CAT_BY_ID[id];
        if (target) Object.assign(target, patch);
      });
    }
    if (state && state.data) {
      Object.values(state.data).forEach(day => {
        ['notes', 'noteAligns'].forEach(field => {
          const old = day[field];
          if (!old) return;
          const keys = Object.keys(old);
          const hasOldKey = keys.some(k => /^\d+$/.test(k));
          if (!hasOldKey) return;
          const next = {};
          keys.forEach(k => {
            if (/^\d+$/.test(k)) {
              const idx = Number(k);
              let tr = -1;
              if (day.tracks) {
                for (const t of [0, 1, 2]) {
                  if (day.tracks[t] && day.tracks[t][idx] != null) { tr = t; break; }
                }
              }
              if (tr === -1) tr = 0;
              next[`${tr}:${idx}`] = old[k];
            } else {
              next[k] = old[k];
            }
          });
          day[field] = next;
        });
      });
    }
    // N36 迁移：早上 6 点拆成半点格（6:00 / 6:30）→ SLOT_COUNT 35→36。
    // 槽位重映射：旧 idx 0–5 原位；旧 idx 6（6:00 整点 1h 格）拆成新 6(6:00)+7(6:30) 同分类
    // （保住 6:00–7:00 那一小时数据，不丢 6:30–7:00）；旧 idx ≥7 整体右移 +1。
    // 幂等：只迁 tracks 长度 35 的旧数据；迁前把整份 localStorage 备份到逃生 key。
    if (state && state.data) {
      const OLD_LEN = 35;
      // 单格/备注/note 用：旧 6:00 落在新 6:00（备注放起点）
      const cellMap = (i) => (i <= 6 ? i : i + 1);
      // 合并块的 start / end：start 同 cellMap；end 要把旧 6（含 6:30）延到新 7
      const mergeStart = (i) => (i <= 6 ? i : i + 1);
      const mergeEnd = (i) => (i < 6 ? i : i + 1);
      const needsMigrate = Object.values(state.data).some(
        d => d && d.tracks && Array.isArray(d.tracks[0]) && d.tracks[0].length === OLD_LEN
      );
      if (needsMigrate) {
        try {
          if (!localStorage.getItem('timeblock-backup-before-6am-split')) {
            localStorage.setItem('timeblock-backup-before-6am-split', raw);
          }
        } catch (e) {}
        Object.values(state.data).forEach(day => {
          if (!day || !day.tracks || !Array.isArray(day.tracks[0]) || day.tracks[0].length !== OLD_LEN) return;
          // tracks：旧 6:00 的分类复制到新 6:00 + 6:30 两格，其余按 cellMap
          day.tracks = day.tracks.map(tr => {
            const next = new Array(OLD_LEN + 1).fill(null);
            (tr || []).forEach((c, i) => {
              if (i === 6) { next[6] = c; next[7] = c; }
              else next[cellMap(i)] = c;
            });
            return next;
          });
          // notes / noteAligns / noteTruncates：key `${t}:${idx}` 的 idx 按 cellMap 右移（备注只跟起点，不复制）
          ['notes', 'noteAligns', 'noteTruncates'].forEach(field => {
            const old = day[field];
            if (!old) return;
            const next = {};
            Object.keys(old).forEach(k => {
              const m = /^(\d+):(\d+)$/.exec(k);
              if (m) next[`${m[1]}:${cellMap(Number(m[2]))}`] = old[k];
              else next[k] = old[k];
            });
            day[field] = next;
          });
          // merges：start/end 槽位重映射（end 把旧 6 延到新 7，覆盖整个 6:00–7:00）
          if (Array.isArray(day.merges)) {
            day.merges = day.merges.map(mg => ({ ...mg, start: mergeStart(mg.start), end: mergeEnd(mg.end) }));
          }
          // pomoLogs 不迁：存的是 startMs/endMs 真实时间，slot 映射运行时按时间重算
        });
      }
    }
    // N58 迁移：22/23 点拆成半点格（22:30 / 23:30）→ SLOT_COUNT 36→38。
    // 旧 idx 0–33 原位；旧 34(22:00)→新 34(22:00)+35(22:30) 同分类；旧 35(23:00)→新 36(23:00)+37(23:30) 同分类。
    // notes 跟起点（22:00→34 / 23:00→36）；merges 的 end 把整点延到后半。幂等：只迁 tracks 长度 36；迁前备份逃生 key。
    // 排在 N36 之后：旧 35 长度数据先被 N36 迁到 36，再由本块迁到 38（链式）。
    if (state && state.data) {
      const OLD_LEN = 36;
      const cellMap = (i) => (i <= 34 ? i : i + 1);            // 旧 35(23:00)→新 36；≤34 原位
      const mergeStart = cellMap;
      const mergeEnd = (i) => ((i === 34 || i === 35) ? cellMap(i) + 1 : cellMap(i)); // 整点块尾延到后半
      const needsMigrate = Object.values(state.data).some(
        d => d && d.tracks && Array.isArray(d.tracks[0]) && d.tracks[0].length === OLD_LEN
      );
      if (needsMigrate) {
        try {
          if (!localStorage.getItem('timeblock-backup-before-2223-split')) {
            localStorage.setItem('timeblock-backup-before-2223-split', raw);
          }
        } catch (e) {}
        Object.values(state.data).forEach(day => {
          if (!day || !day.tracks || !Array.isArray(day.tracks[0]) || day.tracks[0].length !== OLD_LEN) return;
          day.tracks = day.tracks.map(tr => {
            const next = new Array(OLD_LEN + 2).fill(null); // 38
            (tr || []).forEach((c, i) => {
              if (i === 34) { next[34] = c; next[35] = c; }
              else if (i === 35) { next[36] = c; next[37] = c; }
              else next[cellMap(i)] = c;
            });
            return next;
          });
          ['notes', 'noteAligns', 'noteTruncates'].forEach(field => {
            const old = day[field];
            if (!old) return;
            const next = {};
            Object.keys(old).forEach(k => {
              const m = /^(\d+):(\d+)$/.exec(k);
              if (m) next[`${m[1]}:${cellMap(Number(m[2]))}`] = old[k];
              else next[k] = old[k];
            });
            day[field] = next;
          });
          if (Array.isArray(day.merges)) {
            day.merges = day.merges.map(mg => ({ ...mg, start: mergeStart(mg.start), end: mergeEnd(mg.end) }));
          }
        });
      }
    }
    return state;
  } catch (e) {}
  return null;
}

function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
}

const DEFAULT_TWEAKS = {
  palette: 'macaron',
  theme: 'light',
  density: 'comfortable',
  showPomodoro: true,
};

function App() {
  const stored = loadState();
  const initialData = stored?.data || SAMPLE_DATA;
  // [S] 种子机制（seededIds 驱动首次种子 + seedsVersion 驱动展示字段同步）：
  // - 首次种子靠 seededIds（曾种过的 id 集合）判定，与版本号脱钩 —— 每条默认分类这辈子只自动种一次，
  //   用户删掉不会复活（id 仍在 seededIds 里）。修复了开发期 HMR 把 seedsVersion「吃掉」导致漏种的问题。
  // - 展示字段同步（改名/改色）仍按 seedsVersion 触发：同 id 已存在的分类刷成种子最新值，
  //   但用户在 catOverrides 里手动改过的字段一律保留（用户改名 > 种子改名）。
  const SEED_SYNC_FIELDS = ['label', 'name', 'short', 'color', 'desc', 'block'];
  let seededCustomCats;
  let initialSeededIds;
  if (stored?.customCats === undefined) {
    // 全新设备：种入全部默认
    seededCustomCats = [...DEFAULT_CUSTOM_CATS];
    initialSeededIds = DEFAULT_CUSTOM_CATS.map(s => s.id);
  } else {
    seededCustomCats = [...stored.customCats];
    // everSeeded：老存储无 seededIds 字段时，用「当前已存在的默认分类 id」回填——
    // 现存默认项视作已种过；缺失的默认项视作从未种过 → 下面补种一次（仅此一次的「升级补种」）。
    const everSeeded = new Set(
      stored.seededIds
      || seededCustomCats.filter(c => DEFAULT_CUSTOM_CATS.some(s => s.id === c.id)).map(c => c.id)
    );
    // a) 展示字段同步（版本号驱动）：跳过用户 override 过的字段
    if ((stored.seedsVersion || 0) < SEEDS_VERSION) {
      const overrides = stored.catOverrides || {};
      seededCustomCats = seededCustomCats.map(c => {
        const seed = DEFAULT_CUSTOM_CATS.find(s => s.id === c.id);
        if (!seed) return c; // 用户自建分类（非种子）→ 不动
        const userPatch = overrides[c.id] || {};
        const synced = { ...c };
        SEED_SYNC_FIELDS.forEach(f => {
          if (f in seed && !(f in userPatch)) synced[f] = seed[f];
        });
        return synced;
      });
    }
    // b) 首次补种（seededIds 驱动，与版本号无关）：从未种过且当前不存在 → 种入
    DEFAULT_CUSTOM_CATS.forEach(seed => {
      if (!everSeeded.has(seed.id) && !seededCustomCats.find(c => c.id === seed.id)) {
        seededCustomCats.push({ ...seed });
      }
      everSeeded.add(seed.id);
    });
    initialSeededIds = [...everSeeded];
  }
  // 立刻把种子推进 CATEGORIES / CAT_BY_ID，让第一次渲染就能拿到（避免闪烁）
  seededCustomCats.forEach(c => {
    if (!CAT_BY_ID[c.id]) {
      CAT_BY_ID[c.id] = c;
      CATEGORIES.push(c);
    }
  });
  const [data, setData] = useState(initialData);
  const [currentDate, setCurrentDate] = useState(stored?.currentDate || '2026-04-15');
  const [customOpts, setCustomOpts] = useState(stored?.customOpts || { sleep: [], exercise: [] });
  const [customCats, setCustomCats] = useState(seededCustomCats);
  const [seededIds] = useState(initialSeededIds); // 持久化：曾经种过的默认分类 id（首次种子判定用）
  const [catOverrides, setCatOverrides] = useState(stored?.catOverrides || {});

  useEffect(() => {
    (customCats || []).forEach(c => {
      if (!CAT_BY_ID[c.id]) {
        CAT_BY_ID[c.id] = c;
        CATEGORIES.push(c);
      }
    });
    Object.entries(catOverrides).forEach(([id, patch]) => {
      const target = CAT_BY_ID[id];
      if (target) Object.assign(target, patch);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 快捷键一键一主（B16）：把要绑的键先从其他分类身上摘掉，否则按键匹配 CATEGORIES.find
  // 先到先得，排前面的内置分类（如娱乐放松=7）永远抢走后绑的同号键，用户改了也不生效。
  // 摘除走 catOverrides 持久化（内置/自定义通吃），live 引用同步改。
  function releaseKeyFromOthers(id, key) {
    if (!/^[1-9]$/.test(key || '')) return;
    CATEGORIES.forEach(c => {
      if (c.id === id || c.key !== key) return;
      setCatOverrides(o => ({ ...o, [c.id]: { ...(o[c.id] || {}), key: null } }));
      setCustomCats(arr => arr.map(cc => (cc.id === c.id ? { ...cc, key: null } : cc)));
      c.key = null;
    });
  }
  function updateCustomCat(id, patch) {
    if (patch.key) releaseKeyFromOthers(id, patch.key);
    setCustomCats(arr => arr.map(c => c.id === id ? { ...c, ...patch } : c));
    const t = CAT_BY_ID[id]; if (t) Object.assign(t, patch);
  }
  function updateCatOverride(id, patch) {
    if (patch.key) releaseKeyFromOthers(id, patch.key);
    setCatOverrides(o => ({ ...o, [id]: { ...(o[id] || {}), ...patch } }));
    const t = CAT_BY_ID[id]; if (t) Object.assign(t, patch);
  }
  function deleteCustomCat(id) {
    setCustomCats(arr => arr.filter(c => c.id !== id));
    delete CAT_BY_ID[id];
    const idx = CATEGORIES.findIndex(c => c.id === id);
    if (idx >= 0) CATEGORIES.splice(idx, 1);
  }
  function migrateCat(fromId, mode, toId) {
    setData(prevData => {
      const next = {};
      Object.entries(prevData).forEach(([k, d]) => {
        if (!d.tracks) { next[k] = d; return; }
        const newTracks = d.tracks.map(tr => tr.map(c => {
          if (c !== fromId) return c;
          return mode === 'merge' ? toId : null;
        }));
        next[k] = { ...d, tracks: newTracks };
      });
      return next;
    });
  }

  const [catOrder, setCatOrder] = useState(() => {
    // 一次性清理顶层排序里的子项残留：eat 历史上是顶层分类，移到 fun 下后旧 catOrder.root 里残留的
    // 'eat' 会在顶层重复渲染（重复的「吃」）。开机即剔除并随 saveState 存回，彻底抹掉旧数据。
    const co = stored?.catOrder || {};
    if (!Array.isArray(co.root)) return co;
    const childIds = new Set();
    CAT_TREE.forEach(n => (n.children || []).forEach(k => childIds.add(k)));
    (stored?.customCats || []).forEach(c => { if (c.parentId) childIds.add(c.id); });
    const cleanRoot = co.root.filter(id => !childIds.has(id));
    return cleanRoot.length === co.root.length ? co : { ...co, root: cleanRoot };
  });
  // 快速应用面板显示的分类 id 列表（默认 = 原 7 个绑了 key 的）
  const DEFAULT_QUICK_APPLY = ['sleep', 'prep_xnx', 'prep_pr', 'exercise', 'focus', 'eat', 'fun'];
  const [quickApplyIds, setQuickApplyIds] = useState(stored?.quickApplyIds || DEFAULT_QUICK_APPLY);
  const [view, setView] = useState('daily');
  const [selectedCat, setSelectedCat] = useState('prep_pr'); // 默认怦然：睡眠时大多不需要番茄（用户 2026-06-09）
  const [showPomodoro, setShowPomodoro] = useState(false);
  const [drawerMode, setDrawerMode] = useState('closed'); // 移动端 daily-side 抽屉：closed/cat/pomo/both
  // 移动端三轴默认「锁定」：防止上下滑动页面时误拖时间块。锁定=点/拖格子不编辑，竖滑翻页/横滑平移三轴；
  // 解锁=触摸可拖拽编辑（桌面端鼠标精准，恒为解锁，锁按钮 CSS 隐藏，不影响现有行为）。
  const [tracksLocked, setTracksLocked] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 720px)').matches : false
  );
  // 日表下滚把日期栏（.date-bar）滚到 sticky 顶栏下消失时，右上角悬浮一个日期小方块，看三轴时也知道在操作哪天
  const [floatDate, setFloatDate] = useState(false);
  useEffect(() => {
    if (view !== 'daily' || typeof IntersectionObserver === 'undefined') { setFloatDate(false); return; }
    const el = document.querySelector('.date-bar');
    if (!el) return;
    // rootMargin 顶部内缩 ~顶栏高度：日期栏整体滚到顶栏下面才算「不可见」→ 弹方块
    const io = new IntersectionObserver(([e]) => setFloatDate(!e.isIntersecting), { rootMargin: '-64px 0px 0px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [view]);
  const [themePopoverOpen, setThemePopoverOpen] = useState(false);
  const themePopoverRef = useRef(null);
  useEffect(() => {
    if (!themePopoverOpen) return;
    function onDocClick(e) {
      if (themePopoverRef.current && !themePopoverRef.current.contains(e.target)) setThemePopoverOpen(false);
    }
    function onEsc(e) { if (e.key === 'Escape') setThemePopoverOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onEsc); };
  }, [themePopoverOpen]);

  // Undo stack — bounded list of pre-mutation data snapshots. Pushed by
  // applySelection / applyPrevSelection / setNotes / setPrevNotes; popped by undo().
  const HISTORY_LIMIT = 5;
  const [history, setHistory] = useState([]);
  function pushHistory() {
    setHistory(h => {
      const next = [...h, data];
      if (next.length > HISTORY_LIMIT) next.splice(0, next.length - HISTORY_LIMIT);
      return next;
    });
  }
  function undo() {
    setHistory(h => {
      if (h.length === 0) return h;
      const snapshot = h[h.length - 1];
      setData(snapshot);
      return h.slice(0, -1);
    });
  }
  const selectedCatRef = useRef(null);
  // 番茄钟永远写入真实今天（与当前查看的日期解耦），跨午夜在完成时刻按真实时间归档
  const todayKey = dateKey(new Date());
  function onPomoComplete(entry) {
    const id = `pomo_${entry.endMs}`;
    const log = { ...entry, id, applied: false, appliedTrack: null };
    const target = dateKey(new Date(entry.endMs));
    setData(d => {
      const cur = d[target] || emptyDay(target);
      return { ...d, [target]: { ...cur, pomoLogs: [...(cur.pomoLogs || []), log] } };
    });
  }
  const pomo = usePomodoroState(selectedCatRef, onPomoComplete);
  const [pomoAudioChoice, setPomoAudioChoice] = usePomodoroAudioChoice();
  useEffect(() => { selectedCatRef.current = selectedCat; });
  // 把 app-header 实测高度写进 CSS 变量 --app-header-h，供移动端 sticky（mv-nav / 小时表头）定位。
  // 解决「改 header 内容（如隐藏口号）→ 高度变 → 写死的 sticky top(127) 不跟、年份 chips 下方留空白」的耦合坑。
  useEffect(() => {
    const setH = () => {
      const h = document.querySelector('.app-header');
      if (h) document.documentElement.style.setProperty('--app-header-h', h.offsetHeight + 'px');
    };
    setH();
    window.addEventListener('resize', setH);
    return () => window.removeEventListener('resize', setH);
  }, []);
  const [pomoConflict, setPomoConflict] = useState(null);
  const [catPanelCollapsed, setCatPanelCollapsed] = useState(false);
  // 快速应用分类卡片折叠态（持久化：省移动端垂直空间，下次打开记得）
  const [quickApplyCollapsed, setQuickApplyCollapsed] = useState(stored?.quickApplyCollapsed || false);

  function timeRangeToSlotIdxs(startMs, endMs) {
    const sd = new Date(startMs);
    const ed = new Date(endMs);
    const startMin = sd.getHours() * 60 + sd.getMinutes() + sd.getSeconds() / 60;
    const endMin = ed.getHours() * 60 + ed.getMinutes() + ed.getSeconds() / 60;
    const idxs = [];
    SLOTS.forEach((s, i) => {
      const slotStart = s.h * 60 + s.m;
      const slotEnd = slotStart + s.span * 60;
      if (slotEnd > startMin && slotStart < endMin) idxs.push(i);
    });
    return idxs;
  }
  // 番茄随块移动校验：把 slot set 各方向扩 1 格（容差 1 格 = 用户口径"前后稍微移动一格"）
  function pomoSlotsExpand1(slots) {
    const out = new Set();
    slots.forEach(i => {
      out.add(i);
      if (i > 0) out.add(i - 1);
      if (i < SLOT_COUNT - 1) out.add(i + 1);
    });
    return out;
  }
  // 返回该日某轨道指定 slots 范围内已入轴的番茄日志
  function getAttachedPomos(dateK, track, slots) {
    const dayD = data[dateK];
    if (!dayD || !Array.isArray(dayD.pomoLogs)) return [];
    const slotSet = new Set(slots);
    return dayD.pomoLogs.filter(l => {
      if (!l.applied || l.appliedTrack !== track) return false;
      const natural = timeRangeToSlotIdxs(l.startMs, l.endMs);
      return natural.some(i => slotSet.has(i));
    });
  }
  // 校验：旧块上挂着的番茄能否随到新块（natural slot 必须在新块 ±1 格内）
  function validateBlockPomos(dateK, track, oldSlots, newSlots) {
    const attached = getAttachedPomos(dateK, track, oldSlots);
    if (attached.length === 0) return { ok: true, blocked: [], attached };
    const newExpanded = pomoSlotsExpand1(newSlots);
    const blocked = attached.filter(l => {
      const natural = timeRangeToSlotIdxs(l.startMs, l.endMs);
      return !natural.some(i => newExpanded.has(i));
    });
    return { ok: blocked.length === 0, blocked, attached };
  }
  // 清除内容时把挂在这段格子上的番茄解绑回「未入轴」池（applied:false），记录不删、可再放/再删
  function detachAttachedPomos(dateK, track, slots) {
    const attached = getAttachedPomos(dateK, track, slots);
    if (attached.length === 0) return;
    const ids = new Set(attached.map(l => l.id));
    setData(d => {
      const day = d[dateK] || emptyDay(dateK);
      const newLogs = (day.pomoLogs || []).map(l => ids.has(l.id) ? { ...l, applied: false, appliedTrack: null } : l);
      return { ...d, [dateK]: { ...day, pomoLogs: newLogs } };
    });
  }
  // 块换轴后把 attached 番茄的 appliedTrack 迁移到 dstTrack
  function migrateAttachedPomosTrack(dateK, srcTrack, srcSlots, dstTrack) {
    if (srcTrack === dstTrack) return;
    const attached = getAttachedPomos(dateK, srcTrack, srcSlots);
    if (attached.length === 0) return;
    const ids = new Set(attached.map(l => l.id));
    setData(d => {
      const day = d[dateK] || emptyDay(dateK);
      const newLogs = (day.pomoLogs || []).map(l => ids.has(l.id) ? { ...l, appliedTrack: dstTrack } : l);
      return { ...d, [dateK]: { ...day, pomoLogs: newLogs } };
    });
  }
  // 给 DailyTable 的 resize 用：仅检查今日（不含 prev 部分），返回 {ok, blocked, message}
  function validateBlockMoveTodayOnly(track, oldStart, oldEnd, newStart, newEnd) {
    const oldSlots = []; for (let i = oldStart; i <= oldEnd; i++) oldSlots.push(i);
    const newSlots = []; for (let i = newStart; i <= newEnd; i++) newSlots.push(i);
    const res = validateBlockPomos(currentDate, track, oldSlots, newSlots);
    if (!res.ok) return { ok: false, blocked: res.blocked, message: `该时段有 ${res.blocked.length} 条番茄记录不在新位置（1 格以内），无法移动。` };
    return { ok: true };
  }
  // 番茄入轴永远操作真实今天（today），与 currentDate 解耦
  function applyPomoLogs(logIds, forceTrack = null) {
    const target = todayKey;
    const cur = data[target] || emptyDay(target);
    const logs = (cur.pomoLogs || []).filter(l => logIds.includes(l.id) && !l.applied);
    if (logs.length === 0) return;
    if (forceTrack == null) {
      const defaultTrack = 1;
      const hasConflict = logs.some(l => {
        const idxs = timeRangeToSlotIdxs(l.startMs, l.endMs);
        return idxs.some(i => cur.tracks[defaultTrack] && cur.tracks[defaultTrack][i] != null);
      });
      if (hasConflict) {
        setPomoConflict({ logIds });
        return;
      }
      forceTrack = defaultTrack;
    }
    pushHistory();
    setData(d => {
      const day = d[target] || emptyDay(target);
      const newTracks = day.tracks.map(tr => [...tr]);
      const newPomoLogs = [...(day.pomoLogs || [])];
      logs.forEach(l => {
        const idxs = timeRangeToSlotIdxs(l.startMs, l.endMs);
        idxs.forEach(i => { newTracks[forceTrack][i] = l.catId; });
        const li = newPomoLogs.findIndex(x => x.id === l.id);
        if (li >= 0) newPomoLogs[li] = { ...newPomoLogs[li], applied: true, appliedTrack: forceTrack };
      });
      return { ...d, [target]: { ...day, tracks: newTracks, pomoLogs: newPomoLogs } };
    });
  }
  // 入轴冲突逐个处理（N49，用户 2026-06-08）：某番茄选轴→直接入该轴（forceTrack 非空跳过冲突检测）
  // 或跳过（track=null 不入）；从冲突列表移除，列表空则关弹窗
  function resolveConflict(logId, track) {
    if (track != null) applyPomoLogs([logId], track);
    setPomoConflict(prev => {
      if (!prev) return null;
      const rest = prev.logIds.filter(x => x !== logId);
      return rest.length ? { logIds: rest } : null;
    });
  }
  // 默认月份：取 data 里有内容的最近月份（按 dateKey 'YYYY-MM-DD' 字典序排，取最大）
  // 没有任何数据时 fallback 到今天所在月
  const [monthlyYM, setMonthlyYM] = useState(() => {
    const keys = Object.keys(data || {}).sort();
    if (keys.length > 0) {
      const latest = keys[keys.length - 1];
      const [y, m] = latest.split('-').map(Number);
      return { year: y, month: m };
    }
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() + 1 };
  });
  const [monthOpenBlock, setMonthOpenBlock] = useState(null);
  const [t, setTweak] = useTweaks(DEFAULT_TWEAKS);

  useEffect(() => {
    saveState({ data, currentDate, customOpts, customCats, catOverrides, catOrder, quickApplyIds, quickApplyCollapsed, seedsVersion: SEEDS_VERSION, seededIds });
  }, [data, currentDate, customOpts, customCats, catOverrides, catOrder, quickApplyIds, quickApplyCollapsed, seededIds]);

  useEffect(() => {
    if (!customCats || customCats.length === 0) return;
    let changed = false;
    const next = customCats.map(c => {
      if (!c.parentId) return c;
      if (!c.label || c.label.includes('·')) return c;
      const parent = CAT_BY_ID[c.parentId];
      const parentLabel = parent ? String(parent.label || parent.name || '').split('·')[0] : '';
      if (!parentLabel) return c;
      changed = true;
      const newLabel = `${parentLabel}·${c.name || c.label}`;
      const patched = { ...c, label: newLabel };
      if (CAT_BY_ID[c.id]) CAT_BY_ID[c.id].label = newLabel;
      const idx = CATEGORIES.findIndex(x => x.id === c.id);
      if (idx >= 0) CATEGORIES[idx].label = newLabel;
      return patched;
    });
    if (changed) setCustomCats(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addCustom(kind, opt) {
    setCustomOpts(c => ({ ...c, [kind]: [...(c[kind] || []), opt] }));
  }
  function addCustomCat(payload) {
    setCustomCats(arr => {
      const next = [...arr, payload];
      CAT_BY_ID[payload.id] = payload;
      CATEGORIES.push(payload);
      return next;
    });
  }

  useEffect(() => {
    document.documentElement.dataset.theme = t.theme;
    document.documentElement.dataset.palette = t.palette;
  }, [t.theme, t.palette]);

  useEffect(() => {
    if (!data[currentDate]) {
      setData(d => ({ ...d, [currentDate]: emptyDay(currentDate) }));
    }
  }, [currentDate]);

  const day = data[currentDate] || emptyDay(currentDate);

  const prevDateKey = useMemo(() => {
    const d = parseDate(currentDate);
    d.setDate(d.getDate() - 1);
    return dateKey(d);
  }, [currentDate]);
  const prevDay = data[prevDateKey] || emptyDay(prevDateKey);
  function updatePrevDay(newDay) {
    setData(d => ({ ...d, [prevDateKey]: newDay }));
  }
  function setPrevCells(updates) {
    setData(prev => {
      const d = prev[prevDateKey] || emptyDay(prevDateKey);
      const baseTracks = (d.tracks && d.tracks.length === 3)
        ? d.tracks.map(tr => [...tr])
        : [new Array(SLOT_COUNT).fill(null), new Array(SLOT_COUNT).fill(null), new Array(SLOT_COUNT).fill(null)];
      updates.forEach(u => { baseTracks[u.track][u.idx] = u.cat; });
      const notes = { ...(d.notes || {}) };
      const noteAligns = { ...(d.noteAligns || {}) };
      const noteTruncates = { ...(d.noteTruncates || {}) };
      updates.forEach(u => {
        if (u.cat == null) {
          delete notes[`${u.track}:${u.idx}`];
          delete noteAligns[`${u.track}:${u.idx}`];
          delete noteTruncates[`${u.track}:${u.idx}`];
        }
      });
      return { ...prev, [prevDateKey]: { ...emptyDay(prevDateKey), ...d, tracks: baseTracks, notes, noteAligns, noteTruncates } };
    });
  }
  function applyPrevSelection(catId, entries) {
    if (!entries || entries.length === 0) return;
    pushHistory();
    const baseTracks = (prevDay.tracks && prevDay.tracks.length === 3)
      ? prevDay.tracks.map(tr => [...tr])
      : [new Array(SLOT_COUNT).fill(null), new Array(SLOT_COUNT).fill(null), new Array(SLOT_COUNT).fill(null)];
    entries.forEach(e => { baseTracks[e.track][e.idx] = catId; });
    updatePrevDay({ ...emptyDay(prevDateKey), ...prevDay, tracks: baseTracks });
  }
  function setPrevNotes(entries, value, align, truncate) {
    if (!entries || entries.length === 0) return;
    pushHistory();
    setData(prev => {
      const d = prev[prevDateKey] || emptyDay(prevDateKey);
      const notes = { ...(d.notes || {}) };
      const noteAligns = { ...(d.noteAligns || {}) };
      const noteTruncates = { ...(d.noteTruncates || {}) };
      entries.forEach(({ t: tr, i }) => {
        const k = `${tr}:${i}`;
        if (value) {
          notes[k] = value;
          if (align) noteAligns[k] = align;
          if (truncate) noteTruncates[k] = truncate;
        } else {
          delete notes[k];
          delete noteAligns[k];
          delete noteTruncates[k];
        }
      });
      return { ...prev, [prevDateKey]: { ...emptyDay(prevDateKey), ...d, notes, noteAligns, noteTruncates } };
    });
  }

  function updateDay(newDay) {
    setData(d => ({ ...d, [currentDate]: newDay }));
  }
  function setCells(updates) {
    setCellsOn(currentDate, updates);
    // 早上第一段睡眠错轨检测：睡眠/赖床画到了非睡眠轨道 → 弹提示，确认后整段移到睡眠轨道
    // （只在手画/日表路径检测；打卡路径轨道由分类推导，睡眠类必落睡眠轨，不会错轨）
    checkSleepTrackMismatch(updates);
  }
  // setCells 的任意日期版：N38 打卡落库用（永远写真实今天，可能跨午夜写明天）。
  // note 非空时写到每个 update 格上（与快速应用/详情卡"备注写到选区每一格"同口径）
  function setCellsOn(dateK, updates, note) {
    // 用 setData(prev=>...) 避免 stale-closure：同一事件里若先调用其他 setter，这里能读到最新 day
    setData(prev => {
      const d = prev[dateK] || emptyDay(dateK);
      const newTracks = d.tracks.map(tr => [...tr]);
      updates.forEach(u => { newTracks[u.track][u.idx] = u.cat; });
      const touched = {}; updates.forEach(u => { touched[`${u.track}:${u.idx}`] = true; });
      const merges = (d.merges || []).filter(m => {
        for (let i = m.start; i <= m.end; i++) if (touched[`${m.track}:${i}`]) return false;
        return true;
      });
      const notes = { ...d.notes };
      const noteAligns = { ...(d.noteAligns || {}) };
      const noteTruncates = { ...(d.noteTruncates || {}) };
      updates.forEach(u => {
        if (u.cat == null) {
          delete notes[`${u.track}:${u.idx}`];
          delete noteAligns[`${u.track}:${u.idx}`];
          delete noteTruncates[`${u.track}:${u.idx}`];
        } else if (note) {
          notes[`${u.track}:${u.idx}`] = note;
        }
      });
      return { ...prev, [dateK]: { ...d, tracks: newTracks, merges, notes, noteAligns, noteTruncates } };
    });
    // 三餐时间错配检测：填了"吃"分类的 cell，hour 超过对应餐的 cutoff 但 meal 状态还是 ontime → 弹提示
    checkMealMismatch(updates, dateK);
  }
  // N38 打卡落库：复述卡确认后调用。plan.parts = [{ dateK, updates }]（跨午夜时第二段是明天），
  // plan.note = 句子里时间+活动词之外的剩余文字（自动当备注）
  function applyCheckin(plan) {
    pushHistory();
    plan.parts.forEach(p => setCellsOn(p.dateK, p.updates, plan.note));
  }
  // 钟点 → 餐别（与下面 checkMealMismatchOnePoint 的窗口口径一致）
  function mealHourBucket(h) {
    if (h <= 10) return 'breakfast';
    if (h >= 11 && h <= 15) return 'lunch';
    if (h >= 16) return 'dinner';
    return null;
  }
  // 某餐是否已在「按时窗口」内吃过（同餐窗口里有更早的 eat 格子、hour < cutoff）。
  // 用于：已按时吃过的餐，后面同窗口再吃别的（如 8 点吃早餐、10:30 吃早午餐）不该再报这餐拖延。
  function mealHasOnTimeEat(dayState, mealKey, cutoff) {
    if (!dayState || !dayState.tracks) return false;
    return dayState.tracks.some(tr => tr.some((c, i) => {
      if (c !== 'eat') return false;
      const h = SLOTS[i].h;
      return mealHourBucket(h) === mealKey && h < cutoff;
    }));
  }
  // 早餐 9 点前 / 午餐 13 点前 / 晚餐 19 点前 = 按时
  // 通用单点检测：date + hour + cat，prompted set 去重避免同餐多 cell 多次弹
  function checkMealMismatchOnePoint(dateK, hour, cat, prompted, timeLabel) {
    if (cat !== 'eat') return;
    let mealKey, mealLabel, cutoff;
    if (hour <= 10) { mealKey = 'breakfast'; mealLabel = '早餐'; cutoff = 9; }
    else if (hour >= 11 && hour <= 15) { mealKey = 'lunch'; mealLabel = '午餐'; cutoff = 13; }
    else if (hour >= 16) { mealKey = 'dinner'; mealLabel = '晚餐'; cutoff = 19; }
    else return;
    const promptKey = `${dateK}:${mealKey}`;
    if (prompted.has(promptKey)) return;
    prompted.add(promptKey);
    const dayState = data[dateK] || emptyDay(dateK);
    const currentStatus = dayState.meals[mealKey];
    const isLate = currentStatus === 'late';
    const timeStr = timeLabel || `${hour}:00`;
    const datePrefix = dateK !== currentDate ? `${dateK} ` : '';
    if (hour >= cutoff && !isLate) {
      // 这餐已在按时窗口吃过（更早的 eat 格子）→ 当前这格其实是下一餐，别误报这餐拖延
      if (mealHasOnTimeEat(dayState, mealKey, cutoff)) return;
      const ok = window.confirm(`${datePrefix}你在 ${timeStr} 吃东西，已超过${mealLabel}的「${cutoff}点前」按时标准。\n要把${mealLabel}改成「拖延」吗？`);
      if (ok) {
        setData(d => {
          const day = d[dateK] || emptyDay(dateK);
          return { ...d, [dateK]: { ...day, meals: { ...day.meals, [mealKey]: 'late' } } };
        });
      }
    } else if (hour < cutoff && isLate) {
      const ok = window.confirm(`${datePrefix}你在 ${timeStr} 吃东西，在${mealLabel}的「${cutoff}点前」按时范围内。\n${mealLabel}当前是「拖延」，要改回「按时」吗？`);
      if (ok) {
        setData(d => {
          const day = d[dateK] || emptyDay(dateK);
          return { ...d, [dateK]: { ...day, meals: { ...day.meals, [mealKey]: 'ontime' } } };
        });
      }
    }
  }
  // 日表路径：basis on dateK（默认 currentDate；N38 打卡传真实今天）+ slot.idx
  function checkMealMismatch(updates, dateK = currentDate) {
    if (!updates || updates.length === 0) return;
    const prompted = new Set();
    for (const u of updates) {
      if (u.cat !== 'eat') continue;
      const slot = SLOTS[u.idx];
      if (!slot) continue;
      const timeStr = `${slot.h}:${String(slot.m).padStart(2, '0')}`;
      checkMealMismatchOnePoint(dateK, slot.h, u.cat, prompted, timeStr);
    }
  }
  // 早上第一段睡眠错轨检测：睡眠/赖床被画到非睡眠轨道时提示，确认后整段移到睡眠轨道（track 1）。
  // 口径与 dashboard 一致：睡眠类 = sleep 本体 + 名字含「赖床/卧床」的分类。
  function checkSleepTrackMismatch(updates) {
    if (!updates || updates.length === 0) return;
    const day = data[currentDate] || emptyDay(currentDate);
    if (!day.tracks || day.tracks.length < 2) return;
    // data 闭包是 setData 前的旧态；叠加本次 updates 得到画完后的轨道快照
    const tracks = day.tracks.map(tr => [...tr]);
    updates.forEach(u => { if (tracks[u.track]) tracks[u.track][u.idx] = u.cat; });

    const lazyCatIds = new Set();
    CATEGORIES.forEach(c => { if (/赖床|卧床/.test(c.name || c.label || '')) lazyCatIds.add(c.id); });
    const isAsleep = (cat) => cat === 'sleep' || lazyCatIds.has(cat);
    const slotStartMin = (i) => SLOTS[i].h * 60 + SLOTS[i].m;
    const N = SLOTS.length;
    const asleepAt = (i) => tracks.some(tr => isAsleep(tr[i]));

    // 早上第一段睡眠：全天第一个「任一轨道在睡」的格子起，向后连续扩展
    let firstSleepIdx = -1;
    for (let i = 0; i < N; i++) { if (asleepAt(i)) { firstSleepIdx = i; break; } }
    if (firstSleepIdx < 0) return;
    // 第一段睡眠不起于上午 = 不是「早上起来」那段（多半是当晚入睡），不管
    if (slotStartMin(firstSleepIdx) >= 12 * 60) return;
    let endIdx = firstSleepIdx;
    while (endIdx + 1 < N && asleepAt(endIdx + 1)) endIdx++;

    // 这一段里落在「非睡眠轨道」的睡眠格子 = 错位格
    const mismatched = [];
    for (let i = firstSleepIdx; i <= endIdx; i++) {
      for (let t = 0; t < tracks.length; t++) {
        if (t === 1) continue;
        if (isAsleep(tracks[t][i])) mismatched.push({ track: t, idx: i, cat: tracks[t][i] });
      }
    }
    if (mismatched.length === 0) return;
    // 只在「本次刚画的格子」正好命中错位段时才弹，避免对历史数据反复打扰
    const touched = updates.some(u => isAsleep(u.cat) && mismatched.some(m => m.track === u.track && m.idx === u.idx));
    if (!touched) return;

    const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const spanMin = (i) => (SLOTS[i].span === 0.5 ? 30 : 60);
    const minIdx = mismatched.reduce((a, m) => Math.min(a, m.idx), Infinity);
    const maxIdx = mismatched.reduce((a, m) => Math.max(a, m.idx), -Infinity);
    const rangeStr = `${fmt(slotStartMin(minIdx))}–${fmt(slotStartMin(maxIdx) + spanMin(maxIdx))}`;

    const ok = window.confirm(`你在 ${rangeStr} 的睡眠记录不在「睡眠」轨道。\n这是早上第一段睡眠，要移到睡眠轨道吗？\n\n确定 = 移动到睡眠轨道　取消 = 不改`);
    if (!ok) return;

    // 「有冲突就整段不移」：睡眠轨道对应时段有任意一格已占用 → 整段放弃
    const conflict = mismatched.some(m => tracks[1][m.idx] != null);
    if (conflict) {
      window.alert('睡眠轨道在对应时段已有内容，为避免覆盖，整段未移动。\n请先手动清理睡眠轨道后再试。');
      return;
    }

    // 整段移动：错位轨道清空 → 睡眠轨道写入；备注一并迁移
    pushHistory();
    setData(prev => {
      const d = prev[currentDate] || emptyDay(currentDate);
      const newTracks = d.tracks.map(tr => [...tr]);
      const notes = { ...(d.notes || {}) };
      const noteAligns = { ...(d.noteAligns || {}) };
      const noteTruncates = { ...(d.noteTruncates || {}) };
      mismatched.forEach(m => {
        newTracks[1][m.idx] = m.cat;
        if (newTracks[m.track]) newTracks[m.track][m.idx] = null;
        const srcKey = `${m.track}:${m.idx}`;
        const dstKey = `1:${m.idx}`;
        if (notes[srcKey] != null) { notes[dstKey] = notes[srcKey]; delete notes[srcKey]; }
        if (noteAligns[srcKey] != null) { noteAligns[dstKey] = noteAligns[srcKey]; delete noteAligns[srcKey]; }
        if (noteTruncates[srcKey] != null) { noteTruncates[dstKey] = noteTruncates[srcKey]; delete noteTruncates[srcKey]; }
      });
      return { ...prev, [currentDate]: { ...d, tracks: newTracks, notes, noteAligns, noteTruncates } };
    });
  }
  // 月度路径：basis on (date, hour) 列表，可能跨多天
  function checkMealMismatchMonthly(cellsByDateHour) {
    if (!cellsByDateHour || cellsByDateHour.length === 0) return;
    const prompted = new Set();
    for (const { date, hour, cat } of cellsByDateHour) {
      checkMealMismatchOnePoint(date, hour, cat, prompted, `${hour}:00`);
    }
  }
  // Cross-day move — src and dst are both lists of { isPrev, idx } in 1-to-1 correspondence.
  // Handles all 4 cases: today→today, today→prev, prev→today, prev→prev.
  // All reads happen inside the setData updater against `d` (latest state) — no stale closure.
  function moveBlockCrossDay({ srcTrack, srcCells, dstTrack, dstCells, cat }) {
    if (!srcCells || !dstCells || srcCells.length !== dstCells.length) return;
    const len = srcCells.length;
    pushHistory();
    setData(d => {
      const dayState = d[currentDate] || emptyDay(currentDate);
      const prevState = d[prevDateKey] || emptyDay(prevDateKey);
      // Snapshot src notes/aligns BEFORE mutating
      const srcNotes = [], srcAligns = [];
      for (let k = 0; k < len; k++) {
        const sc = srcCells[k];
        const srcDay = sc.isPrev ? prevState : dayState;
        srcNotes[k] = (srcDay.notes || {})[`${srcTrack}:${sc.idx}`];
        srcAligns[k] = (srcDay.noteAligns || {})[`${srcTrack}:${sc.idx}`];
      }
      const newTodayTracks = dayState.tracks.map(tr => [...tr]);
      const newTodayNotes = { ...(dayState.notes || {}) };
      const newTodayAligns = { ...(dayState.noteAligns || {}) };
      const prevTracksBase = (prevState.tracks && prevState.tracks.length === 3)
        ? prevState.tracks.map(tr => [...tr])
        : [new Array(SLOT_COUNT).fill(null), new Array(SLOT_COUNT).fill(null), new Array(SLOT_COUNT).fill(null)];
      const newPrevNotes = { ...(prevState.notes || {}) };
      const newPrevAligns = { ...(prevState.noteAligns || {}) };
      let prevTouched = false, todayTouched = false;
      // Clear src
      srcCells.forEach(sc => {
        if (sc.isPrev) {
          prevTracksBase[srcTrack][sc.idx] = null;
          delete newPrevNotes[`${srcTrack}:${sc.idx}`];
          delete newPrevAligns[`${srcTrack}:${sc.idx}`];
          prevTouched = true;
        } else {
          newTodayTracks[srcTrack][sc.idx] = null;
          delete newTodayNotes[`${srcTrack}:${sc.idx}`];
          delete newTodayAligns[`${srcTrack}:${sc.idx}`];
          todayTouched = true;
        }
      });
      // Drop overlapping merges on today src track
      const todaySrcIdxs = srcCells.filter(c => !c.isPrev).map(c => c.idx);
      const minSrc = todaySrcIdxs.length ? Math.min(...todaySrcIdxs) : null;
      const maxSrc = todaySrcIdxs.length ? Math.max(...todaySrcIdxs) : null;
      const newTodayMerges = (dayState.merges || []).filter(m => {
        if (m.track !== srcTrack) return true;
        if (minSrc == null) return true;
        return m.end < minSrc || m.start > maxSrc;
      });
      // Apply dst writes (after src clear so src/dst overlap doesn't get wiped)
      dstCells.forEach((dc, k) => {
        if (dc.isPrev) {
          prevTracksBase[dstTrack][dc.idx] = cat;
          if (srcNotes[k]) newPrevNotes[`${dstTrack}:${dc.idx}`] = srcNotes[k];
          if (srcAligns[k]) newPrevAligns[`${dstTrack}:${dc.idx}`] = srcAligns[k];
          prevTouched = true;
        } else {
          newTodayTracks[dstTrack][dc.idx] = cat;
          if (srcNotes[k]) newTodayNotes[`${dstTrack}:${dc.idx}`] = srcNotes[k];
          if (srcAligns[k]) newTodayAligns[`${dstTrack}:${dc.idx}`] = srcAligns[k];
          todayTouched = true;
        }
      });
      const next = { ...d };
      if (todayTouched) {
        next[currentDate] = { ...dayState, tracks: newTodayTracks, notes: newTodayNotes, noteAligns: newTodayAligns, merges: newTodayMerges };
      }
      if (prevTouched) {
        next[prevDateKey] = {
          ...emptyDay(prevDateKey),
          ...prevState,
          tracks: prevTracksBase,
          notes: newPrevNotes,
          noteAligns: newPrevAligns,
        };
      }
      return next;
    });
    // 番茄随块迁移：跨轴移动（睡眠轴 ↔ 娱乐/运动轴）这条路径走的是本函数，必须把挂在 srcTrack 的
    // 番茄 appliedTrack 迁到 dstTrack，否则番茄脱钩消失。同 moveBlock 的处理。
    // 仅处理同一天内换轨（today↔today / prev↔prev）；真正跨天且带番茄是边角场景，暂不迁移。
    if (srcTrack !== dstTrack) {
      if (srcCells.every(c => !c.isPrev) && dstCells.every(c => !c.isPrev)) {
        migrateAttachedPomosTrack(currentDate, srcTrack, srcCells.map(c => c.idx), dstTrack);
      } else if (srcCells.every(c => c.isPrev) && dstCells.every(c => c.isPrev)) {
        migrateAttachedPomosTrack(prevDateKey, srcTrack, srcCells.map(c => c.idx), dstTrack);
      }
    }
  }

  function moveBlock(srcTrack, srcStart, srcEnd, dstTrack, dstStart) {
    const len = srcEnd - srcStart + 1;
    const dstEnd = dstStart + len - 1;
    if (dstStart < 0 || dstEnd >= SLOT_COUNT) return;
    if (srcTrack === dstTrack && srcStart === dstStart) return;
    // 番茄校验：旧块上挂着的番茄能否随到新块（±1 格容差）
    const srcSlots = []; for (let i = srcStart; i <= srcEnd; i++) srcSlots.push(i);
    const dstSlots = []; for (let i = dstStart; i <= dstEnd; i++) dstSlots.push(i);
    const pomoCheck = validateBlockPomos(currentDate, srcTrack, srcSlots, dstSlots);
    if (!pomoCheck.ok) {
      window.alert(`该时段有 ${pomoCheck.blocked.length} 条番茄记录不在新位置（1 格以内），无法移动。`);
      return;
    }
    const newTracks = day.tracks.map(tr => [...tr]);
    const srcCells = []; for (let k = 0; k < len; k++) srcCells.push(newTracks[srcTrack][srcStart + k]);
    const dstCells = []; for (let k = 0; k < len; k++) dstCells.push(newTracks[dstTrack][dstStart + k]);
    const dstFirst = dstCells[0];
    const dstAllSame = dstFirst != null && dstCells.every(c => c === dstFirst);
    let isSwap = false;
    if (dstAllSame) {
      const before = dstStart > 0 ? newTracks[dstTrack][dstStart - 1] : null;
      const after = dstEnd + 1 < SLOT_COUNT ? newTracks[dstTrack][dstEnd + 1] : null;
      isSwap = before !== dstFirst && after !== dstFirst;
    }
    const notes = { ...day.notes };
    const noteAligns = { ...(day.noteAligns || {}) };
    const srcNotes = [], srcAligns = [], dstNotes = [], dstAligns = [];
    for (let k = 0; k < len; k++) {
      const sk = `${srcTrack}:${srcStart + k}`;
      const dk = `${dstTrack}:${dstStart + k}`;
      srcNotes[k] = notes[sk]; srcAligns[k] = noteAligns[sk];
      dstNotes[k] = notes[dk]; dstAligns[k] = noteAligns[dk];
      delete notes[sk]; delete noteAligns[sk];
      delete notes[dk]; delete noteAligns[dk];
    }
    for (let k = 0; k < len; k++) newTracks[dstTrack][dstStart + k] = srcCells[k];
    for (let k = 0; k < len; k++) {
      if (srcNotes[k]) notes[`${dstTrack}:${dstStart + k}`] = srcNotes[k];
      if (srcAligns[k]) noteAligns[`${dstTrack}:${dstStart + k}`] = srcAligns[k];
    }
    if (isSwap) {
      for (let k = 0; k < len; k++) newTracks[srcTrack][srcStart + k] = dstCells[k];
      for (let k = 0; k < len; k++) {
        if (dstNotes[k]) notes[`${srcTrack}:${srcStart + k}`] = dstNotes[k];
        if (dstAligns[k]) noteAligns[`${srcTrack}:${srcStart + k}`] = dstAligns[k];
      }
    } else {
      for (let k = 0; k < len; k++) {
        const sIdx = srcStart + k;
        const overlapped = srcTrack === dstTrack && sIdx >= dstStart && sIdx <= dstEnd;
        if (!overlapped) newTracks[srcTrack][sIdx] = null;
      }
    }
    updateDay({ ...day, tracks: newTracks, notes, noteAligns });
    // 番茄随块跟到新轨道（同轨移动 no-op）
    migrateAttachedPomosTrack(currentDate, srcTrack, srcSlots, dstTrack);
    // 三餐时间错配检测：如果移动的是"吃"块，新位置触发同样的检测
    const movedCells = srcCells.map((cat, k) => ({ track: dstTrack, idx: dstStart + k, cat }));
    checkMealMismatch(movedCells);
  }
  function pasteBlock(clip, dstTrack, dstStart) {
    if (!clip) return;
    const len = clip.cells.length;
    if (dstStart < 0 || dstStart + len > SLOT_COUNT) return;
    const newTracks = day.tracks.map(tr => [...tr]);
    const notes = { ...day.notes };
    const noteAligns = { ...(day.noteAligns || {}) };
    for (let k = 0; k < len; k++) {
      newTracks[dstTrack][dstStart + k] = clip.cells[k];
      const dk = `${dstTrack}:${dstStart + k}`;
      if (clip.notes[k]) notes[dk] = clip.notes[k]; else delete notes[dk];
      if (clip.aligns[k]) noteAligns[dk] = clip.aligns[k]; else delete noteAligns[dk];
    }
    updateDay({ ...day, tracks: newTracks, notes, noteAligns });
  }
  // Apply an edit to a monthly-view block (possibly cross-day).
  // - `original`: the block as it was before edit (used to clear cells that are dropped)
  // - `next`:     the block after edit (cells in here get `cat` written; first slot gets `note`)
  // Cells in (original \ next) on the same track are cleared to null.
  // Cells in next get `cat` written (preserving notes on first slot if note provided).
  function applyBlockEdit({ original, next, track, cat, note }) {
    pushHistory();
    // N43：slot 粒度清写（半小时精度）。range 带 startMin/endMin 用之；只有旧整点字段则换算
    const startIdxOf = (rg) => {
      const m = rg.startMin != null ? rg.startMin : rg.startHour * 60;
      return SLOTS.findIndex(sl => sl.h * 60 + sl.m === m);
    };
    const endIdxOf = (rg) => {
      const m = rg.endMin != null ? rg.endMin : (rg.endHour + 1) * 60;
      return SLOTS.findIndex(sl => sl.h * 60 + sl.m + (sl.span === 0.5 ? 30 : 60) === m);
    };
    function* walk(rg) {
      let date = rg.startDate, i = startIdxOf(rg);
      const lastIdx = endIdxOf(rg);
      if (i < 0 || lastIdx < 0) return;
      let safety = 5000;
      while (safety-- > 0) {
        yield [date, i];
        if (date === rg.endDate && i === lastIdx) return;
        if (i === SLOTS.length - 1) {
          i = 0;
          const dObj = parseDate(date); dObj.setDate(dObj.getDate() + 1); date = dateKey(dObj);
        } else i++;
      }
    }
    setData(d => {
      const out = { ...d };
      // Build cell sets（cell = date:slotIdx）
      const newCells = new Set();
      const newCellOrder = [];
      for (const [date, i] of walk(next)) {
        newCells.add(`${date}:${i}`);
        newCellOrder.push([date, i]);
      }
      const oldOnlyCells = [];
      if (original) {
        for (const [date, i] of walk(original)) {
          if (!newCells.has(`${date}:${i}`)) oldOnlyCells.push([date, i]);
        }
      }
      // Clear slots in (original \ next)
      oldOnlyCells.forEach(([date, i]) => {
        const base = out[date] || d[date] || emptyDay(date);
        const newTracks = base.tracks.map(tr => [...tr]);
        const newNotes = { ...(base.notes || {}) };
        newTracks[track][i] = null;
        delete newNotes[`${track}:${i}`];
        out[date] = { ...base, tracks: newTracks, notes: newNotes };
      });
      // Write slots in next（备注只写起点 slot，其余清掉避免残留）
      newCellOrder.forEach(([date, i], cellIdx) => {
        const base = out[date] || d[date] || emptyDay(date);
        const newTracks = base.tracks.map(tr => [...tr]);
        const newNotes = { ...(base.notes || {}) };
        newTracks[track][i] = cat;
        const noteKey = `${track}:${i}`;
        if (cellIdx === 0 && note) newNotes[noteKey] = note;
        else delete newNotes[noteKey];
        out[date] = { ...base, tracks: newTracks, notes: newNotes };
      });
      return out;
    });
  }

  function applySelection(catId, entries) {
    if (!entries || entries.length === 0) return;
    pushHistory();
    const byTrack = { 0: [], 1: [], 2: [] };
    entries.forEach(e => byTrack[e.track].push(e.idx));
    const newTracks = day.tracks.map(tr => [...tr]);
    let merges = (day.merges || []).slice();
    [0, 1, 2].forEach(tr => {
      const idxs = byTrack[tr];
      if (idxs.length === 0) return;
      idxs.forEach(i => { newTracks[tr][i] = catId; });
      const sorted = [...idxs].sort((a, b) => a - b);
      const runs = [];
      let s = sorted[0], p = sorted[0];
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === p + 1) p = sorted[i];
        else { runs.push([s, p]); s = p = sorted[i]; }
      }
      runs.push([s, p]);
      merges = merges.filter(m => {
        if (m.track !== tr) return true;
        return !runs.some(([a, b]) => !(b < m.start || a > m.end));
      });
      if (catId != null) {
        runs.forEach(([a, b]) => { if (b > a) merges.push({ track: tr, start: a, end: b, catId }); });
      }
    });
    updateDay({ ...day, tracks: newTracks, merges });
    // 三餐时间错配检测（applySelection 路径，跟 setCells 共用同一个 check）
    const updatesForCheck = entries.map(e => ({ track: e.track, idx: e.idx, cat: catId }));
    checkMealMismatch(updatesForCheck);
  }
  function setNote(track, idx, value, align, truncate) {
    setNotes([{ t: track, i: idx }], value, align, truncate);
  }
  function setNotes(entries, value, align, truncate) {
    if (!entries || entries.length === 0) return;
    pushHistory();
    setData(prev => {
      const d = prev[currentDate] || emptyDay(currentDate);
      const notes = { ...d.notes };
      const noteAligns = { ...(d.noteAligns || {}) };
      const noteTruncates = { ...(d.noteTruncates || {}) };
      entries.forEach(({ t: tr, i }) => {
        const k = `${tr}:${i}`;
        if (value) {
          notes[k] = value;
          if (align) noteAligns[k] = align;
          if (truncate) noteTruncates[k] = truncate;
        } else {
          delete notes[k];
          delete noteAligns[k];
          delete noteTruncates[k];
        }
      });
      return { ...prev, [currentDate]: { ...d, notes, noteAligns, noteTruncates } };
    });
  }
  function clearDay() {
    if (!confirm('清空当天全部记录？')) return;
    setData(d => { const next = { ...d }; delete next[currentDate]; return next; });
  }

  const [clearEmptyState, setClearEmptyState] = useState(null);
  const [clearEmptyToast, setClearEmptyToast] = useState('');

  function requestClearEmptyDays() {
    const empties = Object.keys(data).filter(k => k !== currentDate && isEmptyDay(data[k])).sort();
    if (empties.length === 0) {
      setClearEmptyToast('没有无数据日期可清除');
      setTimeout(() => setClearEmptyToast(''), 2000);
      return;
    }
    setClearEmptyState({ dates: empties });
  }

  function confirmClearEmptyDays() {
    if (!clearEmptyState) return;
    // A · 二次确认：用 latest data 再过滤一遍（防 modal 打开期间数据被改）
    const verified = clearEmptyState.dates.filter(k =>
      k !== currentDate && data[k] && isEmptyDay(data[k])
    );
    const skipped = clearEmptyState.dates.length - verified.length;
    // C · 备份当前完整 localStorage 状态到独立 key，给用户一个"找回"逃生通道
    try {
      const fullSnapshot = localStorage.getItem('timeblock-app-v1');
      if (fullSnapshot) localStorage.setItem('timeblock-backup-before-clear', fullSnapshot);
    } catch (e) {}
    pushHistory();
    setData(d => {
      const next = { ...d };
      verified.forEach(k => { delete next[k]; });
      return next;
    });
    setClearEmptyState(null);
    const msg = skipped > 0
      ? `已清除 ${verified.length} 个；${skipped} 个二次检查时已不为空，已保留`
      : `已清除 ${verified.length} 个无数据日期（备份已保存）`;
    setClearEmptyToast(msg);
    setTimeout(() => setClearEmptyToast(''), 3000);
  }

  function goDay(delta) {
    const d = parseDate(currentDate);
    d.setDate(d.getDate() + delta);
    setCurrentDate(dateKey(d));
  }

  return (
    <div className="app">
      {/* 番茄钟背景音常驻：与 view/tab 无关，切到任何 tab 都不打断 */}
      <PomodoroAudio mode={pomo.state.mode} runningStartedAt={pomo.state.runningStartedAt} audioChoice={pomoAudioChoice} />
      <header className="app-header">
        <div className="brand">
          <div className="brand-mark" />
          <div className="brand-name">时间块</div>
          <div className="brand-sub">复盘 · 录入 · 洞察</div>
        </div>
        <nav className="nav">
          <button className={`nav-tab ${view === 'daily' ? 'active' : ''}`} onClick={() => setView('daily')}>日表录入</button>
          <button className={`nav-tab ${view === 'monthly' ? 'active' : ''}`} onClick={() => setView('monthly')}>月度时间块</button>
          <button className={`nav-tab ${view === 'album' ? 'active' : ''}`} onClick={() => setView('album')}>时光画册</button>
          <button className={`nav-tab ${view === 'dashboard' ? 'active' : ''}`} onClick={() => setView('dashboard')}>数据洞察</button>
        </nav>
        <div className="header-tools">
          <div className="theme-popover-wrap" ref={themePopoverRef}>
            <button className="icon-btn" title="主题与配色" onClick={() => setThemePopoverOpen(o => !o)}>
              {t.theme === 'light' ? '☀️' : '🌙'}
              <span className="icon-btn-text">主题</span>
            </button>
            {themePopoverOpen && (
              <div className="theme-popover">
                <div className="tp-section-label">模式</div>
                <div className="tp-seg">
                  {['light', 'dark'].map(mode => (
                    <button
                      key={mode}
                      className={`tp-seg-btn ${t.theme === mode ? 'on' : ''}`}
                      onClick={() => setTweak('theme', mode)}
                    >{mode === 'light' ? '☀️ 浅色' : '🌙 深色'}</button>
                  ))}
                </div>
                <div className="tp-section-label">配色</div>
                <div className="tp-palettes">
                  {[
                    { id: 'macaron', label: '马卡龙', dot: '#B49EE0' },
                    { id: 'tiffany', label: '缇芙尼', dot: '#0ABAB5' },
                    { id: 'contrast', label: '高对比', dot: '#1F8A8A' },
                  ].map(p => (
                    <button
                      key={p.id}
                      className={`tp-palette-btn ${t.palette === p.id ? 'on' : ''}`}
                      onClick={() => setTweak('palette', p.id)}
                    >
                      <span className="tp-palette-dot" style={{ background: p.dot }} />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            className={`icon-btn ${(pomo.state.mode === 'running' || pomo.state.mode === 'paused') ? 'pomo-running' : ''}`}
            title={view === 'daily' ? '番茄钟（点击展开/收起）' : '番茄钟（点击回日表录入管理）'}
            onClick={() => {
              if (view !== 'daily') { setView('daily'); setShowPomodoro(true); setDrawerMode('pomo'); return; }
              // 分类面板折叠时番茄钟被 CSS display:none（PC），即便 showPomodoro=true 也看不到 →
              // 视为「未显示」：点击应展开分类面板并显示番茄钟，而不是走隐藏分支白点一下
              const pomoHidden = !showPomodoro || catPanelCollapsed;
              if (pomoHidden) {
                setShowPomodoro(true); setCatPanelCollapsed(false); setDrawerMode('pomo');
              } else {
                setShowPomodoro(false);
                setDrawerMode(m => (m === 'pomo' || m === 'both') ? 'closed' : m);
              }
            }}
          >
            🍅<span className="icon-btn-text">番茄钟</span>
            {(pomo.state.mode === 'running' || pomo.state.mode === 'paused') && (
              <span className="pomo-btn-time">
                {pomo.state.mode === 'paused' ? '⏸' : ''}
                {String(Math.floor(pomo.state.remaining / 60)).padStart(2, '0')}:{String(pomo.state.remaining % 60).padStart(2, '0')}
              </span>
            )}
          </button>
          <details className="icon-btn-dd">
            <summary className="icon-btn icon-btn-dd-summary" title="CSV 导入 / 导出"><LineIcon name="swap" /><span className="icon-btn-text">CSV</span></summary>
            <div className="icon-btn-dd-menu">
              <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; importCsv(data, (next) => setData(next)); }}>↑ 导入 csv</button>
              <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; exportCsv(data); }}>↓ 导出 csv</button>
              <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; exportData3Sheet(data); }}>↓ 导出 xlsx（3 表）</button>
              <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; importCycleHistory(data, (next) => setData(next)); }}>↑ 导入经期历史</button>
              <div className="day-io-dd-sep" />
              <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; exportBackup(); }}>↓ 导出 JSON 全量备份</button>
              <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; importBackup(); }}>↑ 导入 JSON 全量备份</button>
            </div>
          </details>
          <button className="icon-btn" onClick={() => {
            // 选区：
            //   daily   → .daily-table（时间轴 + 推荐事项 + 小时行 + 3 轴色块；不含 grid 的 padding/overflow）
            //   monthly → .monthly-view（顶部时间轴 + 下面色块；不含外侧 wrap padding）
            //   album   → .album（月历主体，不含 view-area wrap padding）
            //   dashboard → .dash-layout（左侧筛选面板 + 右侧主区）
            // 注意：日表要看到"昨晚 21-23"，先点 [昨›] 把 prev tail 展开再截图
            const selMap = { daily: '.daily-table', monthly: '.monthly-view', album: '.album', dashboard: '.dash-layout' };
            const prefixMap = { daily: '日表', monthly: '月度', album: '时光画册', dashboard: '数据洞察' };
            const sel = selMap[view] || '.view-area';
            const prefix = prefixMap[view] || '截图';
            const pad = n => String(n).padStart(2, '0');
            const now = new Date();
            const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
            const hmsStr = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
            let dateSegment;
            if (view === 'daily' && currentDate) {
              dateSegment = `${currentDate}_${todayStr}_${hmsStr}`;
            } else if (view === 'monthly') {
              const ym = `${monthlyYM.year}-${pad(monthlyYM.month)}`;
              dateSegment = `${ym}_${todayStr}_${hmsStr}`;
            } else if (view === 'album') {
              // 从 .album-title 读"{year} 年 {month} 月 · 时光画册"
              const t = document.querySelector('.album-title');
              const m = t && t.textContent.match(/(\d+)\s*年\s*(\d+)\s*月/);
              const ym = m ? `${m[1]}-${pad(+m[2])}` : todayStr.slice(0, 7);
              dateSegment = `${ym}_${todayStr}_${hmsStr}`;
            } else {
              // dashboard / 其他：拼区间（如能从 .dash-sub 拿到）
              let range = '';
              if (view === 'dashboard') {
                const s = document.querySelector('.dash-sub');
                const m = s && s.textContent.match(/(\d{4}-\d{2}-\d{2})\s*→\s*(\d{4}-\d{2}-\d{2})/);
                if (m) range = `${m[1]}_${m[2]}`;
              }
              dateSegment = range ? `${range}_${todayStr}_${hmsStr}` : `${todayStr}_${hmsStr}`;
            }
            // 月度截图 clone 期：① 去掉磨砂控件 ② 中间 + 最底各插一行"0-23 时间数字"对照行（无虚线）
            const prepareClone = view === 'monthly'
              ? (clone) => {
                  clone.querySelectorAll('.mv-frost-control').forEach(el => el.remove());
                  const grid = clone.querySelector('.mv-grid');
                  const hourRow = clone.querySelector('.mv-row-hours');
                  if (grid && hourRow) {
                    const dayRows = grid.querySelectorAll(':scope > .mv-row');
                    const midIdx = Math.floor(dayRows.length / 2);
                    const refNode = dayRows[midIdx];
                    // 中间
                    if (refNode) {
                      const dupMid = hourRow.cloneNode(true);
                      dupMid.classList.add('mv-row-hours-clone');
                      dupMid.style.borderBottom = 'none';
                      dupMid.style.borderTop = 'none';
                      grid.insertBefore(dupMid, refNode);
                    }
                    // 最底
                    const dupBot = hourRow.cloneNode(true);
                    dupBot.classList.add('mv-row-hours-clone');
                    dupBot.style.borderBottom = 'none';
                    dupBot.style.borderTop = 'none';
                    grid.appendChild(dupBot);
                  }
                }
              : undefined;
            exportPng(sel, `${prefix}_${dateSegment}`, prepareClone);
          }} title={view === 'daily' ? '截当前日表 3 轴色块（需要昨晚区先点 [昨›] 展开）' : view === 'monthly' ? '截当前月份的色块图' : '截当前视图'}><LineIcon name="camera" /><span className="icon-btn-text">截图</span></button>
          {/* N38 · 文字打卡：全局入口，落库永远写真实今天（与正在翻看的日期无关） */}
          <CheckinTool data={data} onApply={applyCheckin} />
        </div>
      </header>
      {view === 'daily' && floatDate && (() => {
        const fd = parseDate(currentDate);
        const wk = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][fd.getDay()];
        const isToday = currentDate === todayKey;
        return (
          <button
            className={`float-date ${isToday ? 'is-today' : ''}`}
            title="点击回到顶部日期栏"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <span className="float-date-md">{isToday ? '今·' : ''}{fd.getMonth() + 1}/{fd.getDate()}</span>
            <span className="float-date-wk">{wk}</span>
          </button>
        );
      })()}
      <main className="app-main">
        {view === 'daily' && (
          <div className={`daily-layout ${catPanelCollapsed ? 'side-collapsed' : ''}`}>
            <div className="daily-main view-area">
              <DateBar
                date={currentDate}
                today={todayKey}
                onPrev={() => goDay(-1)}
                onNext={() => goDay(+1)}
                onSet={setCurrentDate}
                dataKeys={Object.keys(data)}
                onClearEmptyDays={requestClearEmptyDays}
              />
              <div className="day-io-bar">
                {/* 三端统一：4 个 xlsx 工具收进一个下拉，省横向空间 */}
                <details className="day-io-dd">
                  <summary className="btn day-io-btn day-io-dd-summary">
                    <span className="day-io-icon">📄</span><span>xlsx 导入导出</span><span className="day-io-dd-caret">▾</span>
                  </summary>
                  <div className="day-io-dd-menu">
                    <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; importTemplate(data, (next) => setData(next)); }}>↑ 导入 xlsx</button>
                    <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; exportTemplateForDate(data, currentDate); }}>↓ 导出当日</button>
                    <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; exportTemplate(data); }}>⇣ 导出全部</button>
                    <button className="day-io-dd-item" onClick={(e) => { e.currentTarget.closest('details').open = false; exportBlankTemplate(); }}>📄 空白模板</button>
                  </div>
                </details>
                <div className="day-io-spacer" />
                <button className="btn day-io-btn danger" title="清空当前选中日期的所有时间块" onClick={() => { if (confirm('确定清空当天所有时间块？此操作不可撤销。')) clearDay(); }}>
                  <span className="day-io-icon">⌫</span>
                  <span>清空全天</span>
                </button>
              </div>
              <div className="daily-meta">
                <DayMetaPanel day={day} onChange={updateDay} customOpts={customOpts} addCustom={addCustom} />
              </div>
              <div className="daily-grid">
                <DailyTable
                  day={day}
                  onCellSet={setCells}
                  onApplySelection={applySelection}
                  onMoveBlock={moveBlock}
                  onMoveBlockCrossDay={moveBlockCrossDay}
                  onMigratePomoTrack={(srcTrack, todaySlots, prevSlots, dstTrack) => {
                    // 详情卡换轴时把挂在这段上的番茄 appliedTrack 一并迁到目标轴（否则番茄脱钩消失）
                    if (todaySlots && todaySlots.length) migrateAttachedPomosTrack(currentDate, srcTrack, todaySlots, dstTrack);
                    if (prevSlots && prevSlots.length) migrateAttachedPomosTrack(prevDateKey, srcTrack, prevSlots, dstTrack);
                  }}
                  onDetachPomos={(track, todaySlots, prevSlots) => {
                    // 清除内容时把这段格子上的番茄解绑回「未入轴」池（applied:false）
                    if (todaySlots && todaySlots.length) detachAttachedPomos(currentDate, track, todaySlots);
                    if (prevSlots && prevSlots.length) detachAttachedPomos(prevDateKey, track, prevSlots);
                  }}
                  onValidateBlockMove={validateBlockMoveTodayOnly}
                  onPasteBlock={pasteBlock}
                  selectedCat={selectedCat}
                  onSelectedCat={setSelectedCat}
                  onNote={setNote}
                  onNotes={setNotes}
                  density={t.density}
                  prevDay={prevDay}
                  prevDateKey={prevDateKey}
                  onPrevCellSet={setPrevCells}
                  onPrevApplySelection={applyPrevSelection}
                  onPrevNotes={setPrevNotes}
                  canUndo={history.length > 0}
                  onUndo={undo}
                  quickApplyIds={quickApplyIds}
                  onQuickApplyChange={setQuickApplyIds}
                  quickApplyCollapsed={quickApplyCollapsed}
                  onQuickApplyCollapsedChange={setQuickApplyCollapsed}
                  defaultQuickApplyIds={DEFAULT_QUICK_APPLY}
                  customCats={customCats}
                  catOrder={catOrder}
                  locked={tracksLocked}
                />
              </div>
            </div>
            {drawerMode !== 'closed' && <div className="daily-side-backdrop" onClick={() => setDrawerMode('closed')} />}
            <aside className={`daily-side ${drawerMode !== 'closed' ? 'drawer-open' : ''} ds-mode-${drawerMode}`}>
              {/* 移动端底部抽屉手柄（PC 隐藏）：切换显示什么。番茄钟单独一档时占满抽屉、完整显示 */}
              <div className="daily-side-handle">
                <span className="dsh-grip" aria-hidden="true" />
                {/* 手柄左侧：当前操作日期（与右侧锁对称）。点击回顶部日期栏 */}
                {(() => {
                  const fd = parseDate(currentDate);
                  const wk = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][fd.getDay()];
                  const isToday = currentDate === todayKey;
                  return (
                    <button
                      type="button"
                      className={`dsh-date ${isToday ? 'is-today' : ''}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      title="点击回到顶部日期栏"
                    >
                      <span className="dsh-date-md">{isToday ? '今·' : ''}{fd.getMonth() + 1}/{fd.getDate()}</span>
                      <span className="dsh-date-wk">{wk}</span>
                    </button>
                  );
                })()}
                <div className="dsh-modes" role="group" aria-label="抽屉内容切换">
                  {/* 正中胶囊：番茄(左) | 都要图标(中) | 时间(右)，扁平 pill 配色 */}
                  <div className="dsh-seg">
                    <button type="button" className={drawerMode === 'pomo' ? 'on' : ''} onClick={() => { setDrawerMode('pomo'); setShowPomodoro(true); setCatPanelCollapsed(false); }}>番茄</button>
                    <button type="button" className={`dsh-seg-icon ${drawerMode === 'both' ? 'on' : ''}`} onClick={() => { setDrawerMode('both'); setShowPomodoro(true); setCatPanelCollapsed(false); }} aria-label="都要（番茄+时间）" title="都要（番茄+时间）">
                      <svg width="16" height="13" viewBox="0 0 16 13" fill="none" aria-hidden="true">
                        <rect x="1" y="1.5" width="6" height="10" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
                        <rect x="9" y="1.5" width="6" height="10" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
                      </svg>
                    </button>
                    <button type="button" className={drawerMode === 'cat' ? 'on' : ''} onClick={() => { setDrawerMode('cat'); setShowPomodoro(false); setCatPanelCollapsed(false); }}>时间</button>
                  </div>
                  {/* 收起：点灰色蒙版区域即可（daily-side-backdrop），不再单独放收起按钮 */}
                </div>
                {/* 三轴锁：贴手柄右侧。🔒=防误触（竖滑翻页/横滑看钟点），🔓=可触摸拖拽编辑 */}
                <button
                  type="button"
                  className={`dsh-lock ${tracksLocked ? 'is-locked' : 'is-unlocked'}`}
                  onClick={() => setTracksLocked(v => !v)}
                  aria-label={tracksLocked ? '三轴已锁定，点击解锁编辑' : '三轴可编辑，点击锁定'}
                  title={tracksLocked ? '三轴已锁定（防误触）：竖滑翻页、横滑看钟点。点击解锁编辑' : '三轴可编辑：触摸拖拽多选/移动块。点击锁定'}
                >
                  {tracksLocked ? '🔒' : '🔓'}
                </button>
              </div>
              {showPomodoro && (
                <Pomodoro
                  currentCat={selectedCat}
                  onSelectedCat={setSelectedCat}
                  state={pomo.state}
                  actions={pomo.actions}
                  pomoLogs={(data[todayKey] && data[todayKey].pomoLogs) || []}
                  onApplyPomoLogs={applyPomoLogs}
                  audioChoice={pomoAudioChoice}
                  onAudioChoice={setPomoAudioChoice}
                  onEditCat={() => {
                    // 切到「都要」并清掉日表的格子选区 → 之后点分类只改番茄的当前分类，
                    // 不会替换掉原本选中时间块的分类（applyToSelection 空选区直接 return）
                    setDrawerMode('both');
                    setCatPanelCollapsed(false);
                    window.dispatchEvent(new CustomEvent('clearCellSelection'));
                    // PC：分类面板可能在折叠区下方/视窗外，滚动让它进视窗（移动端是抽屉、不需要）
                    if (window.innerWidth > 720) {
                      setTimeout(() => {
                        const el = document.querySelector('.daily-side .cat-panel');
                        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 60);
                    }
                  }}
                />
              )}
              <CategoryPanel
                selectedCat={selectedCat}
                onSelectCat={setSelectedCat}
                day={day}
                data={data}
                customCats={customCats}
                addCustomCat={addCustomCat}
                updateCustomCat={updateCustomCat}
                deleteCustomCat={deleteCustomCat}
                catOverrides={catOverrides}
                updateCatOverride={updateCatOverride}
                onMigrateCat={migrateCat}
                catOrder={catOrder}
                setCatOrder={setCatOrder}
                collapsed={catPanelCollapsed}
                onCollapsedChange={setCatPanelCollapsed}
                onApply={(catId) => {
                  window.dispatchEvent(new CustomEvent('applyCat', { detail: { catId } }));
                }}
                onClearAll={clearDay}
              />
            </aside>
          </div>
        )}

        {view === 'monthly' && (
          <div className={`view-area monthly-wrap ${monthOpenBlock ? 'has-panel' : ''}`}>
            <MonthYearNav
              data={data}
              year={monthlyYM.year}
              month={monthlyYM.month}
              onChange={(y, m) => setMonthlyYM({ year: y, month: m })}
            />
            <MonthlyView
              data={data}
              year={monthlyYM.year}
              month={monthlyYM.month}
              onChange={(y, m) => setMonthlyYM({ year: y, month: m })}
              openBlock={monthOpenBlock}
              onOpenBlock={setMonthOpenBlock}
              customCats={customCats}
              catOrder={catOrder}
            />
            {monthOpenBlock && (
              <MonthDetailPanel
                block={monthOpenBlock}
                data={data}
                quickApplyIds={quickApplyIds}
                onQuickApplyChange={setQuickApplyIds}
                defaultQuickApplyIds={DEFAULT_QUICK_APPLY}
                customCats={customCats}
                catOrder={catOrder}
                onClose={() => setMonthOpenBlock(null)}
                onSwitchBlock={setMonthOpenBlock}
                onSave={(payload) => {
                  applyBlockEdit({
                    original: monthOpenBlock.isPlaceholder ? null : {
                      startDate: monthOpenBlock.startDate, startHour: monthOpenBlock.startHour,
                      endDate: monthOpenBlock.endDate, endHour: monthOpenBlock.endHour,
                      startMin: monthOpenBlock.startMin, endMin: monthOpenBlock.endMin,
                    },
                    next: {
                      startDate: payload.startDate, startHour: payload.startHour,
                      endDate: payload.endDate, endHour: payload.endHour,
                      startMin: payload.startMin, endMin: payload.endMin,
                    },
                    track: payload.track,
                    cat: payload.cat,
                    note: payload.note,
                  });
                  setMonthOpenBlock(null);
                }}
              />
            )}
          </div>
        )}

        {view === 'album' && (
          <div className="view-area album-wrap">
            <AlbumTab
              data={data}
              currentDate={currentDate}
              onDayChange={(k, next) => setData(d => ({ ...d, [k]: next }))}
            />
          </div>
        )}

        {view === 'dashboard' && (
          <div className="view-area dash-wrap">
            <Dashboard data={data} anchorDate={parseDate(currentDate)} />
          </div>
        )}
      </main>

      <TweaksPanel title="Tweaks">
        <TweakSection title="主题">
          <TweakRadio label="模式" value={t.theme} options={[{ value: 'light', label: '浅色' }, { value: 'dark', label: '深色' }]} onChange={v => setTweak('theme', v)} />
          <TweakSelect label="配色方案" value={t.palette} options={[{ value: 'macaron', label: '马卡龙原版' }, { value: 'tiffany', label: '缇芙尼主调' }, { value: 'contrast', label: '高对比清爽' }]} onChange={v => setTweak('palette', v)} />
        </TweakSection>
        <TweakSection title="日表">
          <TweakRadio label="密度" value={t.density} options={[{ value: 'compact', label: '紧凑' }, { value: 'comfortable', label: '舒适' }]} onChange={v => setTweak('density', v)} />
        </TweakSection>
      </TweaksPanel>
      {clearEmptyState && (
        <div className="modal-backdrop" onClick={() => setClearEmptyState(null)}>
          <div className="modal clear-empty-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">🧹 清空无数据日期</div>
            <div className="modal-hint">以下 {clearEmptyState.dates.length} 个日期没有任何填写内容，确认清除？</div>
            <div className="clear-empty-list">
              {clearEmptyState.dates.map(k => {
                const dd = parseDate(k);
                const wk = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][dd.getDay()];
                return <div key={k} className="clear-empty-item">• {k}（{wk}）</div>;
              })}
            </div>
            <div className="modal-hint" style={{ marginTop: 10 }}>
              这些日期会从数据中删除，但所有已有数据的日期都不会受影响。<br />
              <span style={{ opacity: 0.8 }}>
                ℹ️ 删除前会自动备份到浏览器；若发现误删，在 DevTools Console 跑：
                <code style={{ display: 'inline-block', padding: '0 4px', background: 'var(--bg-soft)', borderRadius: 4 }}>
                  {`localStorage.setItem('timeblock-app-v1', localStorage.getItem('timeblock-backup-before-clear'))`}
                </code>
                后刷新可恢复（仅保留最近一次备份）。
              </span>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setClearEmptyState(null)}>取消</button>
              <button className="btn primary" onClick={confirmClearEmptyDays}>确认清除 {clearEmptyState.dates.length} 天</button>
            </div>
          </div>
        </div>
      )}
      {clearEmptyToast && (
        <div className="clear-empty-toast">{clearEmptyToast}</div>
      )}
      {pomoConflict && (
        <div className="modal-backdrop" onClick={() => setPomoConflict(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">🍅 入轴冲突（{pomoConflict.logIds.length} 个番茄）</div>
            <div className="modal-hint">默认入睡眠轴，但这些番茄的时段与睡眠轴已有色块冲突。逐个选择去哪条轴：</div>
            <div className="pomo-conflict-list">
              {pomoConflict.logIds.map(id => {
                const log = ((data[todayKey] && data[todayKey].pomoLogs) || []).find(l => l.id === id);
                if (!log) return null;
                const pad2 = n => String(n).padStart(2, '0');
                const t = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
                const dur = log.durationMin >= 60 && log.durationMin % 60 === 0 ? `${log.durationMin / 60}h` : `${log.durationMin}m`;
                return (
                  <div className="pomo-conflict-row" key={id}>
                    <span className="pomo-conflict-time">🍅 {t(log.startMs)}–{t(log.endMs)} · {dur}</span>
                    <div className="pomo-conflict-btns">
                      <button className="btn primary" onClick={() => resolveConflict(id, 0)}>入吃·娱乐</button>
                      <button className="btn" onClick={() => resolveConflict(id, 2)}>入运动</button>
                      <button className="btn danger" onClick={() => resolveConflict(id, 1)}>覆盖睡眠</button>
                      <button className="btn" onClick={() => resolveConflict(id, null)}>跳过</button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="modal-actions"><button className="btn" onClick={() => setPomoConflict(null)}>全部取消</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function DateBar({ date, today, onPrev, onNext, onSet, dataKeys, onClearEmptyDays }) {
  const d = parseDate(date);
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const sortedKeys = useMemo(() => dataKeys.slice().sort(), [dataKeys]);
  const [expandOpen, setExpandOpen] = useState(false);
  const expandRef = useRef(null);
  // 标题即日历入口：点大标题 → 隐藏的原生 date input 弹出选择器（删了原来的 2026/06/18 输入框）
  const pickerRef = useRef(null);
  function openPicker() {
    const el = pickerRef.current;
    if (!el) return;
    try { el.showPicker(); } catch (e) { el.focus(); } // 老浏览器无 showPicker 兜底 focus
  }
  // 「跳到日期」输入框：本地暂存，打字过程中不跳（避免输 2025 时途经 0002/0020/0202 年并建空日期）；
  // 回车 / 失焦才提交，且年份不在 [1900,2200] 视为半截输入 → 回退不跳。
  const [jumpVal, setJumpVal] = useState(date);
  useEffect(() => { setJumpVal(date); }, [date]);
  function commitJump() {
    const y = +String(jumpVal).slice(0, 4);
    if (/^\d{4}-\d{2}-\d{2}$/.test(jumpVal) && y >= 1900 && y <= 2200) onSet(jumpVal);
    else setJumpVal(date);
  }
  // 只显示能在一行放下的最近日期 chip，放不下的进「全部日期」弹窗（不换第二行）
  const recentRef = useRef(null);
  const [maxChips, setMaxChips] = useState(6);
  useEffect(() => {
    const measure = () => {
      const el = recentRef.current;
      if (!el) return;
      // 每个 chip ≈ 62px + gap 4；📅「全部日期」按钮 ≈ 54px。算一行能放几个
      const n = Math.max(0, Math.floor((el.clientWidth - 58) / 66));
      setMaxChips(n);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && recentRef.current) ro.observe(recentRef.current);
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [dataKeys.length]);
  const visibleRecent = sortedKeys.slice(-Math.max(0, maxChips));
  useEffect(() => {
    if (!expandOpen) return;
    function onDocClick(e) {
      if (expandRef.current && !expandRef.current.contains(e.target)) setExpandOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [expandOpen]);
  const expandCols = sortedKeys.length <= 9 ? 3 : sortedKeys.length <= 16 ? 4 : 5;
  const descKeys = sortedKeys.slice().reverse();
  return (
    <div className="date-bar">
      <button className="date-arrow" onClick={onPrev}>‹</button>
      <div
        className="date-block"
        role="button"
        tabIndex={0}
        title="点击选择日期"
        onClick={openPicker}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } }}
      >
        <div className="date-main">{d.getMonth() + 1} 月 {d.getDate()} 日</div>
        <div className="date-meta">{d.getFullYear()} · {weekdays[d.getDay()]}
          <input ref={pickerRef} type="date" className="date-inline-picker" value={date} onChange={e => onSet(e.target.value)} onClick={e => e.stopPropagation()} aria-label="选择日期" />
        </div>
      </div>
      <button className="date-arrow" onClick={onNext}>›</button>
      <div className="date-recent" ref={recentRef}>
        {visibleRecent.map(k => (
          <button key={k} className={`date-chip ${k === date ? 'active' : ''} ${k === today ? 'is-today' : ''}`} onClick={() => onSet(k)}>
            {k === today ? '今·' : ''}{k.slice(5).replace('-', '/')}
          </button>
        ))}
        <div className="date-expand-wrap" ref={expandRef}>
          <button
            className="date-expand-btn"
            onClick={() => setExpandOpen(v => !v)}
            title="全部日期 / 清空无数据日期"
          >
            <LineIcon name="calendar" size={15} /> <span className="date-expand-dots">⋯</span>
          </button>
          {expandOpen && (
            <div className="date-expand-popup">
              <div className="date-expand-popup-head">
                <span>全部日期（共 {sortedKeys.length} 天）</span>
                <button className="date-expand-close" onClick={() => setExpandOpen(false)}>✕</button>
              </div>
              <div className="date-expand-grid" style={{ gridTemplateColumns: `repeat(${expandCols}, 1fr)` }}>
                {descKeys.map(k => (
                  <button
                    key={k}
                    className={`date-chip ${k === date ? 'active' : ''} ${k === today ? 'is-today' : ''}`}
                    onClick={() => { onSet(k); setExpandOpen(false); }}
                  >
                    {k.slice(5).replace('-', '/')}
                  </button>
                ))}
              </div>
              {/* 清空无数据日期移到弹窗最下面一行（脱离日期栏，不再影响三端布局） */}
              <div className="date-expand-foot">
                <button
                  className="btn danger date-clear-empty"
                  onClick={() => { onClearEmptyDays(); setExpandOpen(false); }}
                  title="扫描并删除所有只点开但没填内容的日期"
                >
                  <span className="icon">🧹</span>
                  <span>清空无数据日期</span>
                </button>
                <label className="date-expand-jump">
                  <span className="date-expand-jump-label">跳到日期</span>
                  <input
                    type="date"
                    value={jumpVal}
                    onChange={e => setJumpVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitJump(); } }}
                    onBlur={commitJump}
                    aria-label="输入日期，回车跳转"
                  />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
